/**
 * `/$tenantSlug/$collection/$id/print` — the bare, printable document view.
 *
 * Per ADR-0078 + the 2026-06-12 print design. One shared parametric route
 * renders any document type through @ken/ui's DocumentPrintView.
 *
 * BARE SHELL: the filename uses the trailing-underscore segment
 * (`$tenantSlug_`) so this route parents to the ROOT route, NOT the
 * `$tenantSlug` layout route — there is no sidebar / TopBar / app chrome,
 * exactly what a print/PDF surface needs. The URL is unchanged
 * (`/<tenant>/<collection>/<id>/print`).
 *
 * MODULE GATE: this shared route does not itself call `require_module`.
 * The design accepts a shared parametric route IF the module gate is
 * enforced server-side — and it is: the detail endpoint
 * (`/api/modules/<collection>/<id>`) is wrapped in `require_module` on the
 * L7 router, so a disabled module 403s the data load and the print view
 * shows its error state. Document-type collections only (the inverse of
 * ENTITY_ROUTE_MAP, filtered to printable docs).
 *
 * DATA: the same three-query detail pattern (ADR-0071) as the detail sheet
 * — detail + canonical-field-layout + field-definitions — PLUS the org
 * branding (GET /api/shell/settings/organization) for the letterhead.
 *
 * CreditMemo shares the `invoicing` collection, so the h1 document-type
 * word comes from the LOADED record's entity_type, not the URL.
 *
 * Auto-print fires on data-ready (never before load), and document.title is
 * set to "{DocType}-{friendlyId}" (e.g. "Invoice-111037") so the browser's
 * saved-PDF filename is meaningful and matches the name the invoice
 * Overview's PDF section shows (POP-02).
 */

import { useCurrency } from "@ken/auth";
import {
  DocumentPrintView,
  printAddressFromParts,
  statusTone,
  type DocumentPrintModel,
  type PrintLineItem,
  type PrintMetaRow,
  type PrintPaymentRow,
  type PrintTotalRow,
} from "@ken/ui";
import "@ken/ui/styles/print.css";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";

export const Route = createFileRoute("/$tenantSlug_/$collection/$id/print")({
  component: PrintRoute,
});

// ---------------------------------------------------------------------------
// Collection → printable document type. Inverse of ENTITY_ROUTE_MAP, narrowed
// to document-archetype types. For collections that fold several doc types
// (invoicing = Invoice + CreditMemo), the loaded record's entity_type wins.
// ---------------------------------------------------------------------------

const COLLECTION_DOC_LABEL: Readonly<Record<string, string>> = {
  invoicing: "Invoice",
  sales_receipts: "Sales Receipt",
  estimates: "Estimate",
  purchase_orders: "Purchase Order",
  accounts_payable: "Bill",
  // POP-02: the collections as their detail endpoints are actually mounted
  // (/api/modules/purchase-orders, /api/modules/supplier-bills). The bill
  // page's Print button and the pop-up's Download PDF open these.
  "purchase-orders": "Purchase Order",
  "supplier-bills": "Bill",
};

/** Pascal entity_type → friendly document-type word for the h1. */
const ENTITY_TYPE_DOC_LABEL: Readonly<Record<string, string>> = {
  Invoice: "Invoice",
  CreditMemo: "Credit Memo",
  SalesReceipt: "Sales Receipt",
  Estimate: "Estimate",
  PurchaseOrder: "Purchase Order",
  Bill: "Bill",
};

// Status → semantic tone now resolves through the SHARED statusTone()
// (ADR-0083) imported from @ken/ui — the print route's local copy is retired
// so list, story header, and print agree on one mapping.

interface DetailResponse {
  id: string;
  source_ref: string;
  entity_type?: string;
  core: Record<string, unknown>;
  custom?: Record<string, unknown>;
  lines?: Array<Record<string, unknown>>;
  payments?: Array<Record<string, unknown>>;
  applications?: Array<Record<string, unknown>>;
}

interface OrgResponse {
  branding: DocumentPrintModel["branding"];
  currency: string;
}

async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`${url} → ${res.status}`);
  return (await res.json()) as T;
}

function num(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
function str(v: unknown): string | null {
  return v === null || v === undefined ? null : String(v);
}

function PrintRoute(): ReactElement {
  const { tenantSlug, collection, id } = useParams({
    from: "/$tenantSlug_/$collection/$id/print",
  });
  const currency = useCurrency();

  const [detail, setDetail] = useState<DetailResponse | null>(null);
  const [org, setOrg] = useState<OrgResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const printedRef = useRef(false);

  const docCollection = COLLECTION_DOC_LABEL[collection];

  useEffect(() => {
    if (!docCollection) {
      setError("This kind of record can't be printed.");
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        // Detail loads through the module's require_module-gated endpoint,
        // so a disabled module 403s here (server-side module gate).
        const [d, o] = await Promise.all([
          getJson<DetailResponse>(`/api/modules/${collection}/${id}`),
          getJson<OrgResponse>("/api/shell/settings/organization").catch(() => ({
            branding: {},
            currency,
          })),
        ]);
        // Canonical layout + field defs are part of the three-query pattern;
        // the print view renders the document shape (not the dynamic tabs),
        // so we fetch them to honour the contract but tolerate their absence.
        await Promise.allSettled([
          getJson(`/api/modules/${collection}/canonical-field-layout`),
          getJson(`/api/modules/${collection}/field-definitions`),
        ]);
        if (cancelled) return;
        setDetail(d);
        setOrg(o);
        setReady(true);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Couldn't load this document.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [collection, id, docCollection, currency]);

  const model = useMemo<DocumentPrintModel | null>(() => {
    if (!detail) return null;
    const core = detail.core ?? {};
    // CreditMemo shares the invoicing collection — the loaded entity_type
    // (not the URL) decides the document word.
    const entityType = detail.entity_type ?? "";
    const docTypeLabel =
      ENTITY_TYPE_DOC_LABEL[entityType] ?? docCollection ?? "Document";

    const friendlyId =
      str(core["invoice_number"]) ??
      str(core["credit_memo_number"]) ??
      str(core["document_number"]) ??
      str(core["number"]) ??
      detail.source_ref ??
      "";

    const lines: PrintLineItem[] = (detail.lines ?? []).map((ln) => ({
      description:
        str(ln["product_name"]) ??
        str(ln["service_name"]) ??
        str(ln["description"]) ??
        "—",
      quantity: num(ln["quantity"]),
      unitPrice: num(ln["unit_price"]),
      amount: num(ln["amount"]),
    }));

    const subtotal = num(core["subtotal"]);
    const tax = num(core["tax_amount"]) ?? num(core["tax"]);
    const total = num(core["total_amount"]) ?? num(core["total"]);
    const totals: PrintTotalRow[] = [];
    if (subtotal != null) totals.push({ label: "Subtotal", amount: subtotal });
    if (tax != null) totals.push({ label: "Tax", amount: tax });
    if (total != null) totals.push({ label: "Total", amount: total, strong: true });

    const payments: PrintPaymentRow[] = (detail.payments ?? []).map((p) => ({
      date: str(p["payment_date"]),
      method: str(p["payment_method"]),
      amount: num(p["amount"]),
    }));

    const meta: PrintMetaRow[] = [
      { label: "Date", value: str(core["invoice_date"]) ?? str(core["txn_date"]) ?? str(core["document_date"]), isDate: true },
      { label: "Due date", value: str(core["due_date"]), isDate: true },
    ].filter((m) => m.value);

    const balanceDue = num(core["balance_due"]) ?? num(core["remaining_credit"]);

    return {
      docTypeLabel,
      friendlyId,
      statusLabel: str(core["status"]),
      statusTone: statusTone(str(core["status"])),
      archivedAt: str(core["archived_at"]),
      branding: org?.branding ?? {},
      tenantName: tenantSlug,
      // POP-02: the addresses the document holds, built from their parts.
      parties: [
        {
          heading: "Bill to",
          name: str(core["customer_name"]) ?? str(core["supplier_name"]),
          address:
            printAddressFromParts(core, "billing") ??
            printAddressFromParts(core, "supplier"),
        },
        ...(printAddressFromParts(core, "shipping")
          ? [{ heading: "Ship to", address: printAddressFromParts(core, "shipping") }]
          : []),
      ],
      meta,
      lines,
      totals,
      payments,
      balanceDue,
      memo: str(core["memo"]) ?? str(core["notes"]),
      currency: org?.currency ?? currency,
    };
  }, [detail, org, docCollection, tenantSlug, currency]);

  // document.title → meaningful saved-PDF filename, set once data is ready.
  useEffect(() => {
    if (model && typeof document !== "undefined") {
      // "Invoice-111037" — the browser saves "Invoice-111037.pdf", the name
      // the invoice Overview's PDF section shows (POP-02).
      document.title = [model.docTypeLabel.replace(/\s+/g, "-"), model.friendlyId]
        .filter(Boolean)
        .join("-");
    }
  }, [model]);

  // Auto-print ONLY after data is ready (never before load).
  useEffect(() => {
    if (ready && model && !printedRef.current && typeof window !== "undefined") {
      printedRef.current = true;
      // Defer a tick so the DOM (and images) paint before the dialog.
      const t = window.setTimeout(() => window.print(), 300);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [ready, model]);

  if (error) {
    return (
      <div
        role="alert"
        data-testid="print-error"
        className="mx-auto max-w-[8.5in] p-8 text-sm text-[var(--color-status-overdue-text)]"
      >
        {error}
      </div>
    );
  }

  if (!model) {
    return (
      <div
        data-testid="print-loading"
        aria-busy="true"
        className="mx-auto max-w-[8.5in] p-8 text-sm text-[var(--color-text-soft)]"
      >
        Preparing document…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-surface-soft)] py-6 print:bg-white print:py-0">
      {/* Screen-only toolbar — never printed (print.css hides print:hidden). */}
      <div className="print:hidden mx-auto max-w-[8.5in] mb-4 flex justify-end gap-2 px-2">
        <button
          type="button"
          onClick={() => window.print()}
          className="h-10 px-4 rounded-[var(--radius-md)] bg-[var(--color-brand)] text-[var(--color-text-on-dark)] text-sm font-medium"
        >
          Print
        </button>
        <button
          type="button"
          onClick={() => window.close()}
          className="h-10 px-4 rounded-[var(--radius-md)] border border-[var(--color-border-hairline)] bg-[var(--color-surface)] text-sm font-medium"
        >
          Close
        </button>
      </div>
      <DocumentPrintView model={model} />
    </div>
  );
}
